import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../api/client';
import { PropertyMap } from '../components/properties/PropertyMap';
import { Loading, Modal, ScoreBadge } from '../components/common';
import type {
  InvestmentProperty,
  InvestmentPropertyFilters,
  InvestmentPropertyState,
  LoanType,
  MapBounds,
  PropertyCluster,
} from '../types';

// Price options for dropdown (0 to 1M in 100K increments, then larger)
const PRICE_OPTIONS = [
  { value: 0, label: 'Any' },
  { value: 50000, label: '$50K' },
  { value: 100000, label: '$100K' },
  { value: 150000, label: '$150K' },
  { value: 200000, label: '$200K' },
  { value: 250000, label: '$250K' },
  { value: 300000, label: '$300K' },
  { value: 350000, label: '$350K' },
  { value: 400000, label: '$400K' },
  { value: 450000, label: '$450K' },
  { value: 500000, label: '$500K' },
  { value: 600000, label: '$600K' },
  { value: 700000, label: '$700K' },
  { value: 800000, label: '$800K' },
  { value: 900000, label: '$900K' },
  { value: 1000000, label: '$1M' },
  { value: 1500000, label: '$1.5M' },
  { value: 2000000, label: '$2M+' },
];
import './InvestmentPropertiesPage.css';

// Format currency
const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);

// Format percentage (handles string or number from API)
const formatPct = (n: number | string | null | undefined) => {
  const num = Number(n);
  return isNaN(num) ? 'N/A' : `${num.toFixed(1)}%`;
};

// Get property image URL with R2 preference, fallback to original
const getImageUrl = (property: InvestmentProperty): string | undefined => {
  if (property.r2ImageKey) {
    return `https://property-api.aelieve.workers.dev/api/images/${property.r2ImageKey}`;
  }
  return property.mainImageUrl;
};

// Zoom threshold: below this, use server-side clusters; at/above this, load individual properties
const CLUSTER_ZOOM_THRESHOLD = 10;

export function InvestmentPropertiesPage() {
  // State
  const [properties, setProperties] = useState<InvestmentProperty[]>([]);
  const [clusters, setClusters] = useState<PropertyCluster[]>([]);
  const [states, setStates] = useState<InvestmentPropertyState[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<InvestmentProperty | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [loanType, setLoanType] = useState<LoanType>('CONV');
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState<string>('score');
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
  const [mapZoom, setMapZoom] = useState(4); // Track map zoom level
  const [filterByMap, setFilterByMap] = useState(true); // Enabled by default like Zillow
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  // Use clusters when zoomed out, individual properties when zoomed in
  const useServerClusters = mapZoom < CLUSTER_ZOOM_THRESHOLD;

  // Filters
  const [filters, setFilters] = useState<InvestmentPropertyFilters>({
    minPrice: 0,
    maxPrice: 0, // 0 = "Any" (no limit)
    minBeds: 0,
    minScore: 0,
    minCapRate: 0,
    minCoC: 0,
    limit: 500, // Limit for individual properties when zoomed in
  });

  // Load available states
  useEffect(() => {
    api.getInvestmentPropertyStates().then(({ states }) => {
      setStates(states);
    });
  }, []);

  // Load properties or clusters when filters, map bounds, or zoom change
  useEffect(() => {
    const loadData = async () => {
      if (!mapBounds) return; // Need bounds for both modes

      setLoading(true);
      try {
        const requestFilters = { ...filters, ...mapBounds };

        if (useServerClusters) {
          // At low zoom, fetch server-side clusters (fast, ~400 clusters max)
          const response = await api.getInvestmentPropertyClusters({
            ...requestFilters,
            gridSize: 20, // 20x20 grid = up to 400 clusters
          });
          setClusters(response.clusters);
          setProperties([]); // Clear individual properties
          setTotal(response.total);
        } else {
          // At high zoom, fetch individual properties (limited to visible area)
          const response = await api.getInvestmentProperties(requestFilters);
          setProperties(response.properties);
          setClusters([]); // Clear clusters
          setTotal(response.total);
        }
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoading(false);
      }
    };

    // Debounce bounds/zoom changes to avoid rapid API calls while panning/zooming
    const timeoutId = setTimeout(loadData, 300);
    return () => clearTimeout(timeoutId);
  }, [filters, mapBounds, useServerClusters]);

  // Get cashflow based on loan type
  const getCashflow = useCallback(
    (property: InvestmentProperty): number => {
      switch (loanType) {
        case 'FHA':
          return property.fhaMonthlyCashflow;
        case 'CONV':
          return property.convMonthlyCashflow;
        case 'DSCR':
          return property.dscrMonthlyCashflow;
      }
    },
    [loanType]
  );


  // Get cash to close based on loan type
  const getCashToClose = useCallback(
    (property: InvestmentProperty): number => {
      switch (loanType) {
        case 'FHA':
          return property.fhaCashToClose;
        case 'CONV':
          return property.convCashToClose;
        case 'DSCR':
          return property.dscrCashToClose;
      }
    },
    [loanType]
  );

  // Get cashflow for a specific property based on current loan type
  const getPropertyCashflow = useCallback(
    (property: InvestmentProperty): number => {
      switch (loanType) {
        case 'FHA':
          return property.fhaMonthlyCashflow;
        case 'CONV':
          return property.convMonthlyCashflow;
        case 'DSCR':
          return property.dscrMonthlyCashflow;
      }
    },
    [loanType]
  );

  // Get CoC for a specific property based on current loan type
  // CoC = (Annual Cashflow / Cash to Close) * 100
  const getPropertyCoC = useCallback(
    (property: InvestmentProperty): number => {
      let cashflow: number;
      let cashToClose: number;
      switch (loanType) {
        case 'FHA':
          cashflow = property.fhaMonthlyCashflow;
          cashToClose = property.fhaCashToClose;
          break;
        case 'CONV':
          cashflow = property.convMonthlyCashflow;
          cashToClose = property.convCashToClose;
          break;
        case 'DSCR':
          cashflow = property.dscrMonthlyCashflow;
          cashToClose = property.dscrCashToClose;
          break;
      }
      if (!cashToClose || cashToClose === 0) return 0;
      return (cashflow * 12) / cashToClose * 100;
    },
    [loanType]
  );

  // Filter and sort properties (bounds filtering now handled by API)
  const sortedProperties = useMemo(() => {
    let filtered = [...properties];

    // Filter by Cap Rate (client-side since API doesn't support this yet)
    if (filters.minCapRate && filters.minCapRate > 0) {
      filtered = filtered.filter(p => Number(p.capRatePct) >= filters.minCapRate!);
    }

    // Filter by CoC (client-side since it depends on loan type selection)
    if (filters.minCoC && filters.minCoC > 0) {
      filtered = filtered.filter(p => getPropertyCoC(p) >= filters.minCoC!);
    }

    // Note: Map bounds filtering is now handled server-side via API

    // Sort
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'cashflow':
          return Number(getPropertyCashflow(b)) - Number(getPropertyCashflow(a));
        case 'coc':
          return Number(getPropertyCoC(b)) - Number(getPropertyCoC(a));
        case 'cap_rate':
          return Number(b.capRatePct) - Number(a.capRatePct);
        case 'price_asc':
          return Number(a.listPrice) - Number(b.listPrice);
        case 'price_desc':
          return Number(b.listPrice) - Number(a.listPrice);
        case 'rent':
          return Number(b.monthlyRent) - Number(a.monthlyRent);
        default: // 'score'
          return Number(b.investmentScore) - Number(a.investmentScore);
      }
    });
  }, [properties, sortBy, filters.minCapRate, filters.minCoC, getPropertyCashflow, getPropertyCoC]);

  // Paginate for sidebar list (map shows all)
  const totalPages = Math.ceil(sortedProperties.length / ITEMS_PER_PAGE);
  const paginatedProperties = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedProperties.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedProperties, currentPage]);

  // Reset to page 1 when filters/sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortBy, filterByMap, mapBounds]);

  // Handle property click from map
  const handlePropertySelect = (property: InvestmentProperty) => {
    setSelectedProperty(property);
    // Scroll to property in list
    const element = document.getElementById(`property-${property.id}`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // Handle property click from list
  const handlePropertyClick = (property: InvestmentProperty) => {
    setSelectedProperty(property);
    setShowDetail(true);
  };

  // Handle map bounds and zoom change
  const handleBoundsChange = useCallback((bounds: MapBounds, zoom: number) => {
    setMapBounds(bounds);
    setMapZoom(zoom);
  }, []);

  return (
    <div className="investment-page">
      {/* Filters Bar */}
      <div className="filters-bar">
        <div className="filters-row">
          <div className="filter-group">
            <label>State</label>
            <select
              value={filters.stateCode || ''}
              onChange={(e) =>
                setFilters({ ...filters, stateCode: e.target.value || undefined })
              }
            >
              <option value="">All States</option>
              {states.map((s) => (
                <option key={s.stateCode} value={s.stateCode}>
                  {s.stateCode} ({s.count.toLocaleString()})
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Min Price</label>
            <select
              value={filters.minPrice || 0}
              onChange={(e) =>
                setFilters({ ...filters, minPrice: parseInt(e.target.value) || 0 })
              }
            >
              {PRICE_OPTIONS.map((opt) => (
                <option key={`min-${opt.value}`} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Max Price</label>
            <select
              value={filters.maxPrice || 0}
              onChange={(e) =>
                setFilters({ ...filters, maxPrice: parseInt(e.target.value) || undefined })
              }
            >
              {PRICE_OPTIONS.map((opt) => (
                <option key={`max-${opt.value}`} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Bedrooms</label>
            <select
              value={filters.minBeds || 0}
              onChange={(e) =>
                setFilters({ ...filters, minBeds: parseInt(e.target.value) || 0 })
              }
            >
              <option value="0">Any</option>
              <option value="1">1+</option>
              <option value="2">2+</option>
              <option value="3">3+</option>
              <option value="4">4+</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Min Score</label>
            <select
              value={filters.minScore || 0}
              onChange={(e) =>
                setFilters({ ...filters, minScore: parseFloat(e.target.value) || 0 })
              }
            >
              <option value="0">Any</option>
              <option value="5">5+</option>
              <option value="6">6+</option>
              <option value="7">7+</option>
              <option value="8">8+</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Min Cap %</label>
            <select
              value={filters.minCapRate || 0}
              onChange={(e) =>
                setFilters({ ...filters, minCapRate: parseFloat(e.target.value) || 0 })
              }
            >
              <option value="0">Any</option>
              <option value="4">4%+</option>
              <option value="5">5%+</option>
              <option value="6">6%+</option>
              <option value="7">7%+</option>
              <option value="8">8%+</option>
              <option value="10">10%+</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Min CoC %</label>
            <select
              value={filters.minCoC || 0}
              onChange={(e) =>
                setFilters({ ...filters, minCoC: parseFloat(e.target.value) || 0 })
              }
            >
              <option value="0">Any</option>
              <option value="5">5%+</option>
              <option value="8">8%+</option>
              <option value="10">10%+</option>
              <option value="12">12%+</option>
              <option value="15">15%+</option>
              <option value="20">20%+</option>
            </select>
          </div>

          <div className="filter-group loan-type-group">
            <label>Loan Type</label>
            <div className="loan-type-buttons">
              {(['FHA', 'CONV', 'DSCR'] as LoanType[]).map((type) => (
                <button
                  key={type}
                  className={`loan-btn ${loanType === type ? 'active' : ''}`}
                  onClick={() => setLoanType(type)}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <label>Sort by</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="score">Investment Score</option>
              <option value="cashflow">Monthly Cashflow</option>
              <option value="coc">Cash-on-Cash %</option>
              <option value="cap_rate">Cap Rate</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
              <option value="rent">Monthly Rent</option>
            </select>
          </div>

          <div className="filter-group filter-checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={filterByMap}
                onChange={(e) => setFilterByMap(e.target.checked)}
              />
              Filter by Map
            </label>
          </div>
        </div>

        <div className="results-count">
          Showing {sortedProperties.length.toLocaleString()} of {total.toLocaleString()} properties
          {filterByMap && mapBounds && ` (filtered to map view)`}
        </div>
      </div>

      {/* Split View */}
      <div className="split-view">
        {/* Map Panel */}
        <div className="map-panel">
          <PropertyMap
            properties={sortedProperties}
            clusters={clusters}
            selectedProperty={selectedProperty}
            onPropertySelect={handlePropertySelect}
            loanType={loanType}
            onBoundsChange={handleBoundsChange}
            filterByMap={filterByMap}
          />
        </div>

        {/* Properties List */}
        <div className="list-panel">
          {loading ? (
            <div className="loading-container">
              <Loading message="Loading properties..." />
            </div>
          ) : (
            <div className="property-list">
              {paginatedProperties.map((property) => (
                <div
                  key={property.id}
                  id={`property-${property.id}`}
                  className={`property-row ${
                    selectedProperty?.id === property.id ? 'selected' : ''
                  }`}
                  onClick={() => handlePropertyClick(property)}
                >
                  <div className="property-image">
                    {getImageUrl(property) ? (
                      <img src={getImageUrl(property)} alt={property.fullAddress} />
                    ) : (
                      <div className="no-image">No Image</div>
                    )}
                    <div className="score-badge-overlay">
                      <ScoreBadge score={Number(property.investmentScore) * 10} size="sm" />
                    </div>
                  </div>

                  <div className="property-info">
                    <div className="property-price">{formatCurrency(property.listPrice)}</div>
                    <div className="property-address">{property.fullAddress}</div>
                    <div className="property-details">
                      {property.bedrooms}bd · {property.bathrooms}ba ·{' '}
                      {property.sqft?.toLocaleString()} sqft
                    </div>
                    <div className="property-metrics">
                      <span className="metric">
                        Rent: <strong>{formatCurrency(property.monthlyRent)}</strong>
                      </span>
                      <span className="metric">
                        Cap: <strong>{formatPct(property.capRatePct)}</strong>
                      </span>
                    </div>
                  </div>

                  <div className="property-loan">
                    <div className="loan-label">{loanType}</div>
                    <div
                      className={`cashflow ${
                        getCashflow(property) >= 0 ? 'positive' : 'negative'
                      }`}
                    >
                      {formatCurrency(getCashflow(property))}/mo
                    </div>
                    <div className="cash-to-close">
                      {formatCurrency(getCashToClose(property))} down
                    </div>
                  </div>
                </div>
              ))}

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Prev
                  </button>
                  <span className="pagination-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Property Detail Modal */}
      <Modal
        isOpen={showDetail && !!selectedProperty}
        onClose={() => setShowDetail(false)}
        title={selectedProperty?.fullAddress || 'Property Details'}
      >
        {selectedProperty && (
          <PropertyDetailView
            property={selectedProperty}
            loanType={loanType}
            onClose={() => setShowDetail(false)}
          />
        )}
      </Modal>
    </div>
  );
}

// Property Detail View Component
function PropertyDetailView({
  property,
  loanType,
  onClose,
}: {
  property: InvestmentProperty;
  loanType: LoanType;
  onClose: () => void;
}) {
  return (
    <div className="property-detail-view">
      <div className="detail-header">
        <div className="detail-main">
          <h2>{formatCurrency(property.listPrice)}</h2>
          <p className="detail-address">{property.fullAddress}</p>
          <p className="detail-specs">
            {property.bedrooms}bd · {property.bathrooms}ba · {property.sqft?.toLocaleString()} sqft
            {property.yearBuilt && ` · Built ${property.yearBuilt}`}
          </p>
        </div>
        <div className="detail-score">
          <ScoreBadge score={Number(property.investmentScore) * 10} size="md" />
        </div>
      </div>

      {getImageUrl(property) && (
        <div className="detail-image">
          <img src={getImageUrl(property)} alt={property.fullAddress} />
        </div>
      )}

      <div className="detail-sections">
        {/* Rent & Yield */}
        <div className="detail-section">
          <h3>Rent & Returns</h3>
          <div className="metrics-grid">
            <div className="metric-item">
              <span className="label">Monthly Rent</span>
              <span className="value">{formatCurrency(property.monthlyRent)}</span>
            </div>
            <div className="metric-item">
              <span className="label">Gross Yield</span>
              <span className="value">{formatPct(property.grossYieldPct)}</span>
            </div>
            <div className="metric-item">
              <span className="label">Cap Rate</span>
              <span className="value">{formatPct(property.capRatePct)}</span>
            </div>
            <div className="metric-item">
              <span className="label">Rent Source</span>
              <span className="value">{property.rentSource}</span>
            </div>
          </div>
        </div>

        {/* Loan Comparison */}
        <div className="detail-section">
          <h3>Loan Comparison</h3>
          <div className="loan-comparison">
            <div className={`loan-card ${loanType === 'FHA' ? 'selected' : ''}`}>
              <h4>FHA (3.5% Down)</h4>
              <div className="loan-metrics">
                <div className="loan-metric">
                  <span>Down Payment</span>
                  <span>{formatCurrency(property.fhaDownPayment)}</span>
                </div>
                <div className="loan-metric">
                  <span>Monthly PITI</span>
                  <span>{formatCurrency(property.fhaMonthlyPiti)}</span>
                </div>
                <div className="loan-metric">
                  <span>Cash to Close</span>
                  <span>{formatCurrency(property.fhaCashToClose)}</span>
                </div>
                <div
                  className={`loan-metric cashflow ${
                    property.fhaMonthlyCashflow >= 0 ? 'positive' : 'negative'
                  }`}
                >
                  <span>Monthly Cashflow</span>
                  <span>{formatCurrency(property.fhaMonthlyCashflow)}</span>
                </div>
              </div>
            </div>

            <div className={`loan-card ${loanType === 'CONV' ? 'selected' : ''}`}>
              <h4>Conventional (20% Down)</h4>
              <div className="loan-metrics">
                <div className="loan-metric">
                  <span>Down Payment</span>
                  <span>{formatCurrency(property.convDownPayment)}</span>
                </div>
                <div className="loan-metric">
                  <span>Monthly PITI</span>
                  <span>{formatCurrency(property.convMonthlyPiti)}</span>
                </div>
                <div className="loan-metric">
                  <span>Cash to Close</span>
                  <span>{formatCurrency(property.convCashToClose)}</span>
                </div>
                <div
                  className={`loan-metric cashflow ${
                    property.convMonthlyCashflow >= 0 ? 'positive' : 'negative'
                  }`}
                >
                  <span>Monthly Cashflow</span>
                  <span>{formatCurrency(property.convMonthlyCashflow)}</span>
                </div>
              </div>
            </div>

            <div className={`loan-card ${loanType === 'DSCR' ? 'selected' : ''}`}>
              <h4>DSCR (25% Down)</h4>
              <div className="loan-metrics">
                <div className="loan-metric">
                  <span>Down Payment</span>
                  <span>{formatCurrency(property.dscrDownPayment)}</span>
                </div>
                <div className="loan-metric">
                  <span>Monthly PITI</span>
                  <span>{formatCurrency(property.dscrMonthlyPiti)}</span>
                </div>
                <div className="loan-metric">
                  <span>Cash to Close</span>
                  <span>{formatCurrency(property.dscrCashToClose)}</span>
                </div>
                <div
                  className={`loan-metric cashflow ${
                    property.dscrMonthlyCashflow >= 0 ? 'positive' : 'negative'
                  }`}
                >
                  <span>Monthly Cashflow</span>
                  <span>{formatCurrency(property.dscrMonthlyCashflow)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Expenses */}
        <div className="detail-section">
          <h3>Monthly Expenses</h3>
          <div className="expense-breakdown">
            <div className="expense-row">
              <span>Property Tax</span>
              <span>{formatCurrency(property.monthlyTax)}</span>
            </div>
            <div className="expense-row">
              <span>Insurance</span>
              <span>{formatCurrency(property.monthlyInsurance)}</span>
            </div>
            <div className="expense-row">
              <span>Maintenance</span>
              <span>{formatCurrency(property.monthlyMaintenance)}</span>
            </div>
          </div>
        </div>

        {/* ZIP Scores */}
        <div className="detail-section">
          <h3>ZIP Code Analysis</h3>
          <div className="zip-scores">
            <div className="score-item">
              <span className="score-label">Overall</span>
              <span className="score-value">{Number(property.zipScore).toFixed(1)}</span>
            </div>
            <div className="score-item">
              <span className="score-label">Rent Yield</span>
              <span className="score-value">{Number(property.rentYieldScore).toFixed(1)}</span>
            </div>
            <div className="score-item">
              <span className="score-label">Appreciation</span>
              <span className="score-value">{Number(property.zipAppreciationScore).toFixed(1)}</span>
            </div>
            <div className="score-item">
              <span className="score-label">Vacancy</span>
              <span className="score-value">{Number(property.vacancyScore).toFixed(1)}</span>
            </div>
            <div className="score-item">
              <span className="score-label">Income</span>
              <span className="score-value">{Number(property.incomeScore).toFixed(1)}</span>
            </div>
          </div>
          {property.zipMedianIncome && (
            <p className="zip-income">
              Median Income: {formatCurrency(property.zipMedianIncome)}
            </p>
          )}
        </div>
      </div>

      <div className="detail-actions">
        <a
          href={`https://www.roofstock.com/investment-property-marketplace/${property.urlFormattedAddress}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary"
        >
          View on Roofstock
        </a>
        <button onClick={onClose} className="btn-secondary">
          Close
        </button>
      </div>
    </div>
  );
}
